import { useEffect, useRef } from "react";
import { useState } from "react"
import { toast } from "react-toastify";
import { get, post } from "../../controllers/apiController";
import Layout from "../../components/Layout";
import { Select2WColor, Select2 } from "../../components/Select2";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { formatPhoneTR } from "../../utils/phone";
import { downloadBlob, buildVcf } from "../../utils/download";
import { getProjectLabel } from "../../utils/project";

const formatDate = (value) => {
    if (!value) return '';

    const date = new Date(value);

    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('tr-TR');
};

// Excel hücre sınırı 32767 karakter
const PROJECT_OPTIONS = [
    { value: 'all', label: 'Tümü' },
    { value: 'levent', label: 'Levent' },
    { value: 'gokturk', label: 'Göktürk' }
];

const formatNotes = (notes, showProject) => (notes || [])
    .map(n => `${showProject && n.Proje ? `{${getProjectLabel(n.Proje)}} ` : ''}[${n.Not_Tarihi || ''}] ${n.Not_Basligi || ''}${n.Kullanici ? ` (${n.Kullanici.Kullanici_Adi} ${n.Kullanici.Kullanici_Soyadi})` : ''}: ${n.Not_Aciklamasi || ''}`)
    .join('\n')
    .slice(0, 32000);

const AcenteRaporOlustur = () => {
    const [labels, setLabels] = useState([]);
    const [temsilciler, setTemsilciler] = useState([]);

    const [selectedLabels, setSelectedLabels] = useState(null);
    const [selectedTemsilciler, setSelectedTemsilciler] = useState(null);

    const [loading, setLoading] = useState(true);

    const [report, setReport] = useState(null);
    const [filteredAgencies, setFilteredAgencies] = useState([]);

    const [showReportModal, setShowReportModal] = useState(false);
    // Kullanıcı ID -> ad. Bir ID için API'ye en fazla bir kez istek atılır.
    const userNameCache = useRef(new Map());

    const [excelLoading, setExcelLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("all");

    const [project, setProject] = useState("all");
    const [reportProject, setReportProject] = useState("all");

    const [startDate, setStartDate] = useState(null);
    const [lastDate, setLastDate] = useState(null);

    useEffect(() => {
        document.title = "Acente Rapor Oluştur | LEVELUP CRM";

        const getLabels = async () => {
            try {
                const response = await get("labels/");

                const labelsMapped = response.map(etiket => {
                    return {
                        value: etiket.Etiket_ID,
                        label: etiket.Etiket_Adi,
                        color: "#" + etiket.Etiket_Renk
                    }
                });

                setLabels(labelsMapped);
            } catch (err) {
                toast.error("Etiketler alınırken bir hata oluştu!");
            }
        }

        const getTemsilciler = async () => {
            try {
                const response = await get("users/company-users");

                const temsilcilerMapped = response.map(temsilci => {
                    return {
                        value: temsilci.Kullanici_ID,
                        label: `${temsilci.Kullanici_Adi} ${temsilci.Kullanici_Soyadi}`
                    }
                });

                temsilcilerMapped.forEach(t => userNameCache.current.set(t.value, t.label));

                setTemsilciler(temsilcilerMapped);
            } catch (err) {
                toast.error("Temsilci verileri alınırken bir hata oluştu!");
            }
        }

        const load = async () => {
            await Promise.all([getLabels(), getTemsilciler()]);

            setLoading(false);
        }

        load();
    }, []);

    const resolveUserNames = async (ids) => {
        const missing = [...new Set(ids)].filter(id => !userNameCache.current.has(id));

        await Promise.all(missing.map(async id => {
            try {
                const user = await get(`users/company-users/${id}`);
                userNameCache.current.set(id, `${user.Kullanici_Adi} ${user.Kullanici_Soyadi}`);
            } catch (err) {
                userNameCache.current.set(id, '');
            }
        }));
    }

    const createReport = async () => {
        try {
            const body = { project };

            if (startDate) body.startDate = startDate;
            if (lastDate) body.lastDate = lastDate;
            if (selectedLabels != null && selectedLabels.length > 0) body.labelIds = selectedLabels.map(label => label.value);
            if (selectedTemsilciler != null && selectedTemsilciler.length > 0) body.representativeIds = selectedTemsilciler.map(temsilci => temsilci.value);

            const [response, agencies] = await Promise.all([post("reports/agencies", body), get("acenteler", { project })]);

            const labelIds = body.labelIds || [];
            const representativeIds = body.representativeIds || [];
            const visitedIds = new Set(response.visitedAgencies.map(a => a.Acente_ID));

            const reportAgencyIds = new Set(response.agencyIds);

            const matchedAgencies = agencies
                .filter(a => {
                    const agencyLabelIds = (a.Etiketler || '').split(',').map(id => parseInt(id, 10));

                    return reportAgencyIds.has(a.Acente_ID)
                        && (labelIds.length === 0 || labelIds.some(id => agencyLabelIds.includes(id)))
                        && (representativeIds.length === 0 || representativeIds.includes(a.Temsilci_ID));
                });

            await resolveUserNames(matchedAgencies.map(a => a.Temsilci_ID).filter(id => id != null));

            setFilteredAgencies(matchedAgencies
                .map(a => ({
                    id: a.Acente_ID,
                    name: a.Acente_Adi,
                    phone: a.Telefon || '',
                    email: a.Email || '',
                    uyruk: a.Uyruk || '',
                    temsilci: userNameCache.current.get(a.Temsilci_ID) || '',
                    etiketler: (a.Etiketler || '').split(',')
                        .map(id => labels.find(l => l.value === parseInt(id, 10))?.label)
                        .filter(Boolean)
                        .join(', '),
                    aramaSayisi: a.Arama_Sayisi || 0,
                    tarih: formatDate(a.Son_Arama_Tarihi),
                    hatirlatma: formatDate(a.Hatirlatma_Tarihi),
                    olusturma: formatDate(a.Olusturma_Tarihi),
                    aciklama: a.Aciklama || '',
                    visited: visitedIds.has(a.Acente_ID)
                })));
            setActiveTab("all");
            setReportProject(project);

            setReport(response);

            setShowReportModal(true);
        } catch (err) {
            toast.error("Rapor oluşturulurken bir hata oluştu.");
        }
    }

    const fileStamp = () => new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');

    const fetchNotes = async (agencies) => {
        const notesById = new Map();

        for (let i = 0; i < agencies.length; i += 10) {
            await Promise.all(agencies.slice(i, i + 10).map(async a => {
                try {
                    const detail = await get(`acenteler/${a.id}`, { project: reportProject });
                    notesById.set(a.id, formatNotes(detail.Notlar, reportProject === 'all'));
                } catch (err) {
                    notesById.set(a.id, '');
                }
            }));
        }

        return notesById;
    }

    const downloadExcel = async () => {
        setExcelLoading(true);

        try {
            const notesById = await fetchNotes(filteredAgencies);

            const wb = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
                { 'BİLGİ': 'Proje', 'DEĞER': PROJECT_OPTIONS.find(o => o.value === reportProject)?.label },
                { 'BİLGİ': 'Toplam Acente Sayısı', 'DEĞER': report.totalAgencies },
                { 'BİLGİ': 'Ziyaret Eden Acente Sayısı', 'DEĞER': report.visitedCount },
                { 'BİLGİ': 'Ziyaret Etmeyen Acente Sayısı', 'DEĞER': report.totalAgencies - report.visitedCount }
            ]), "Özet");

            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report.representativeReport.map(r => ({
                'TEMSİLCİ ADI': r.Temsilci_Adi,
                'ACENTE SAYISI': r.AcenteSayisi,
                'ZİYARET SAYISI': r.ZiyaretSayisi
            }))), "Temsilci Raporu");

            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report.labelReport.map(r => ({
                'ETİKET': r.Etiket_Adi,
                'ACENTE SAYISI': r.AcenteSayisi
            }))), "Etiket Raporu");

            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filteredAgencies.map(a => ({
                'ACENTE ADI': a.name,
                'TELEFON': a.phone,
                'E-POSTA': a.email,
                'UYRUK': a.uyruk,
                'TEMSİLCİ': a.temsilci,
                'ETİKETLER': a.etiketler,
                'ARAMA SAYISI': a.aramaSayisi,
                'SON ARAMA TARİHİ': a.tarih,
                'HATIRLATMA TARİHİ': a.hatirlatma,
                'OLUŞTURMA TARİHİ': a.olusturma,
                'AÇIKLAMA': a.aciklama,
                'GÖRÜŞME NOTLARI': notesById.get(a.id) || '',
                'ZİYARET': a.visited ? 'Evet' : 'Hayır'
            }))), "Acenteler");

            const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            downloadBlob(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Acente_Raporu_${reportProject}_${fileStamp()}.xlsx`);
        } catch (err) {
            toast.error("Excel oluşturulurken bir hata oluştu.");
        } finally {
            setExcelLoading(false);
        }
    }

    const downloadVcf = async () => {
        try {
            const contacts = filteredAgencies
                .filter(a => a.phone)
                .map(a => ({ name: a.name, phone: formatPhoneTR(a.phone).tel }));

            if (contacts.length === 0) {
                toast.warning("Telefon numarası olan acente bulunamadı.");
                return;
            }

            downloadBlob(new Blob([buildVcf(contacts)], { type: 'text/vcard;charset=utf-8' }), `Acente_Rehber_${fileStamp()}.vcf`);
        } catch (err) {
            toast.error("Rehber dosyası oluşturulurken bir hata oluştu.");
        }
    }

    const paginationComponentOptions = {
        rowsPerPageText: 'Sayfa başına satır:',
        rangeSeparatorText: 'toplam',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Tümü',
        firstPageText: 'İlk',
        previousPageText: 'Önceki',
        nextPageText: 'Sonraki',
        lastPageText: 'Son',
        noRowsPerPage: false,
    };

    const customStyles = {
        noData: {
            style: {
                padding: '24px',
                textAlign: 'center',
            },
        },
        rows: {
            style: {
                padding: '20px 0',
            },
        },
        modalBackdropStyle: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1040
        }
    };

    const visitedAgenciesColumns = [
        {
            name: 'ACENTE ADI',
            cell: row => (
                <a href={`./agency-detail?id=${row.Acente_ID}`}>{row.Acente_Adi}</a>
            ),
            sortable: true
        }
    ];

    const agencyColumns = [
        {
            name: 'ACENTE ADI',
            cell: row => (
                <a href={`./agency-detail?id=${row.id}`}>{row.name}</a>
            ),
            sortable: true
        },
        {
            name: 'TELEFON',
            selector: row => row.phone,
            sortable: true
        },
        {
            name: 'E-POSTA',
            selector: row => row.email,
            sortable: true
        },
        {
            name: 'UYRUK',
            selector: row => row.uyruk,
            sortable: true
        },
        {
            name: 'TEMSİLCİ',
            selector: row => row.temsilci,
            sortable: true
        },
        {
            name: 'ETİKETLER',
            selector: row => row.etiketler,
            sortable: true
        },
        {
            name: 'ARAMA SAYISI',
            selector: row => row.aramaSayisi,
            sortable: true
        },
        {
            name: 'ZİYARET',
            selector: row => (row.visited ? 'Evet' : 'Hayır'),
            sortable: true
        }
    ];

    const representativeReportColumns = [
        {
            name: 'TEMSİLCİ ADI',
            selector: row => row.Temsilci_Adi,
            sortable: true
        },
        {
            name: 'ACENTE SAYISI',
            selector: row => row.AcenteSayisi,
            sortable: true
        },
        {
            name: 'ZİYARET SAYISI',
            selector: row => row.ZiyaretSayisi,
            sortable: true
        }
    ];

    const labelReportColumns = [
        {
            name: 'ETİKET',
            cell: row => (
                <div className="badge" style={{ backgroundColor: `#${row.Etiket_Renk}`, color: `#${row.Etiket_Yazi_Renk}` }}>
                    {row.Etiket_Adi}
                </div>
            ),
            sortable: true
        },
        {
            name: 'ACENTE SAYISI',
            selector: row => row.AcenteSayisi,
            sortable: true
        }
    ];

    return (
        <Layout>
            <main className="nxl-container">
                {loading ? (
                    <div>Yükleniyor...</div>
                ) : (
                    <div className="nxl-content">
                        <div className="page-header">
                            <div className="page-header-left d-flex align-items-center">
                                <div className="page-header-title">
                                    <h5 className="m-b-10">LEVELUP CRM</h5>
                                </div>
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><a href="./">Anasayfa</a></li>
                                    <li className="breadcrumb-item">Acente Rapor Oluştur</li>
                                </ul>
                            </div>
                        </div>
                        <div className="main-content">
                            <div className="row">
                                <div className="col-xxl-12 col-md-12">
                                    <div className="card stretch stretch-full">
                                        <div className="card-header">
                                            <h5 className="card-title">Acente Rapor Oluştur</h5>
                                        </div>
                                        <div className="card-body custom-card-action p-4">
                                            <div className="row mb-4 align-items-center">
                                                <label className="fw-semibold mb-2">Proje: </label>
                                                <div className="p-2">
                                                    <select className="form-control" id="project" value={project} onChange={(e) => setProject(e.target.value)}>
                                                        {PROJECT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="row mb-4 align-items-center">
                                                <label className="fw-semibold mb-2">Etiket (<b>Zorunlu değil</b>): </label>
                                                <Select2WColor
                                                    value={selectedLabels}
                                                    key={"labelSelect"}
                                                    options={labels}
                                                    isSearchable={true}
                                                    isClearable={true}
                                                    isMulti={true}
                                                    onChange={(e) => setSelectedLabels(e)}
                                                    style={{ padding: 0 }}
                                                />
                                            </div>
                                            <div className="row mb-4 align-items-center">
                                                <label className="fw-semibold mb-2">Temsilci (<b>Zorunlu değil</b>): </label>
                                                <Select2
                                                    value={selectedTemsilciler}
                                                    key={"temsilciSelect"}
                                                    options={temsilciler}
                                                    isSearchable={true}
                                                    isClearable={true}
                                                    isMulti={true}
                                                    onChange={(e) => setSelectedTemsilciler(e)}
                                                    style={{ padding: 0 }}
                                                />
                                            </div>
                                            <div className="row mb-4 align-items-center">
                                                <label className="fw-semibold mb-2">Başlangıç Tarihi: </label>
                                                <div className="p-2">
                                                    <input type="date" className="form-control" id="startDate" onChange={(e) => setStartDate(e.target.value)} value={startDate} />
                                                </div>
                                            </div>
                                            <div className="row mb-4 align-items-center">
                                                <label className="fw-semibold mb-2">Bitiş Tarihi: </label>
                                                <div className="p-2">
                                                    <input type="date" className="form-control" id="lastDate" onChange={(e) => setLastDate(e.target.value)} value={lastDate} />
                                                </div>
                                            </div>
                                            <button onClick={createReport} className="btn btn-primary">
                                                <i className="feather-plus me-2"></i>
                                                <span>Rapor Oluştur</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {showReportModal && report && (
                            <div className="modal fade show" style={{ display: 'block' }}>
                                <div className="modal-dialog" style={{ maxWidth: '90%' }}>
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Acente Raporu</h5>
                                            <button type="button" className="btn-close" onClick={() => setShowReportModal(false)}></button>
                                        </div>
                                        <div className="modal-body" style={{ maxHeight: '100vh', overflowY: 'auto' }}>
                                            <div className="row">
                                                <div className="col-xxl-4 col-md-6">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div className="avatar-text avatar-xl rounded">
                                                                        <i className="feather-briefcase"></i>
                                                                    </div>
                                                                    <a href="javascript:void(0);" className="fw-bold d-block">
                                                                        <span className="d-block">Toplam Acente Sayısı</span>
                                                                        <span className="fs-24 fw-bolder d-block">{report.totalAgencies}</span>
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xxl-4 col-md-6">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div className="avatar-text avatar-xl rounded">
                                                                        <i className="feather-check-circle"></i>
                                                                    </div>
                                                                    <a href="javascript:void(0);" className="fw-bold d-block">
                                                                        <span className="d-block">Ziyaret Eden Acente Sayısı</span>
                                                                        <span className="fs-24 fw-bolder d-block">{report.visitedCount}</span>
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xxl-4 col-md-6">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div className="avatar-text avatar-xl rounded">
                                                                        <i className="feather-x-circle"></i>
                                                                    </div>
                                                                    <a href="javascript:void(0);" className="fw-bold d-block">
                                                                        <span className="d-block">Ziyaret Etmeyen Acente Sayısı</span>
                                                                        <span className="fs-24 fw-bolder d-block">{report.totalAgencies - report.visitedCount}</span>
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <ul className="nav nav-tabs mt-4">
                                                <li className="nav-item">
                                                    <button type="button" className={`nav-link ${activeTab === "all" ? "active" : ""}`} onClick={() => setActiveTab("all")}>Tümü</button>
                                                </li>
                                                <li className="nav-item">
                                                    <button type="button" className={`nav-link ${activeTab === "visited" ? "active" : ""}`} onClick={() => setActiveTab("visited")}>Ziyaret Eden Acenteler</button>
                                                </li>
                                            </ul>
                                            {activeTab === "all" && (
                                                <div>
                                                    <div className="d-flex gap-2 mt-3">
                                                        <button type="button" className="btn btn-success" onClick={downloadExcel} disabled={excelLoading}>
                                                            <i className="feather-download me-2"></i>{excelLoading ? "Hazırlanıyor..." : "Excel İndir"}
                                                        </button>
                                                                    <button type="button" className="btn btn-info" onClick={downloadVcf}>
                                                            <i className="feather-phone me-2"></i>Rehber (VCF) İndir
                                                        </button>
                                                    </div>
                                            <div className="row mt-2">
                                                <div className="col-xxl-6 col-md-12">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-header">
                                                            <h5 className="card-title">Temsilci Raporu</h5>
                                                        </div>
                                                        <div className="card-body p-0">
                                                            <DataTable
                                                                columns={representativeReportColumns}
                                                                data={report.representativeReport}
                                                                className="table table-hover mb-0"
                                                                pagination
                                                                responsive
                                                                noDataComponent="Görüntülenecek kayıt bulunamadı"
                                                                paginationComponentOptions={paginationComponentOptions}
                                                                customStyles={customStyles}
                                                                paginationPerPage={10}
                                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xxl-6 col-md-12">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-header">
                                                            <h5 className="card-title">Etiket Raporu</h5>
                                                        </div>
                                                        <div className="card-body p-0">
                                                            <DataTable
                                                                columns={labelReportColumns}
                                                                data={report.labelReport}
                                                                className="table table-hover mb-0"
                                                                pagination
                                                                responsive
                                                                noDataComponent="Görüntülenecek kayıt bulunamadı"
                                                                paginationComponentOptions={paginationComponentOptions}
                                                                customStyles={customStyles}
                                                                paginationPerPage={10}
                                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row mt-2">
                                                <div className="col-xxl-12 col-md-12">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-header">
                                                            <h5 className="card-title">Acenteler ({filteredAgencies.length})</h5>
                                                        </div>
                                                        <div className="card-body p-0">
                                                            <DataTable
                                                                columns={agencyColumns}
                                                                data={filteredAgencies}
                                                                className="table table-hover mb-0"
                                                                pagination
                                                                responsive
                                                                noDataComponent="Görüntülenecek kayıt bulunamadı"
                                                                paginationComponentOptions={paginationComponentOptions}
                                                                customStyles={customStyles}
                                                                paginationPerPage={10}
                                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                                </div>
                                            )}
                                            {activeTab === "visited" && (
                                                <div>
                                            <div className="row mt-2">
                                                <div className="col-xxl-12 col-md-12">
                                                    <div className="card stretch stretch-full">
                                                        <div className="card-header">
                                                            <h5 className="card-title">Ziyaret Eden Acenteler</h5>
                                                        </div>
                                                        <div className="card-body p-0">
                                                            <DataTable
                                                                columns={visitedAgenciesColumns}
                                                                data={report.visitedAgencies}
                                                                className="table table-hover mb-0"
                                                                pagination
                                                                responsive
                                                                noDataComponent="Görüntülenecek kayıt bulunamadı"
                                                                paginationComponentOptions={paginationComponentOptions}
                                                                customStyles={customStyles}
                                                                paginationPerPage={10}
                                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" onClick={() => setShowReportModal(false)}>Kapat</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {showReportModal && (
                            <div style={customStyles.modalBackdropStyle} onClick={() => setShowReportModal(false)} />
                        )}
                    </div>
                )}
            </main>
        </Layout >
    );
}

export default AcenteRaporOlustur;
